import {
    BLOCKS_BY_KIND,
    ContentSection,
    createEmptyBlock,
    ToolContentKind,
    ToolDetailBlock,
    ToolDetailBlockType,
} from '@easytools/tool-content';

/**
 * Sezioni pronte da inserire: struttura e stile già impostati, testi vuoti. Nell'editor i
 * campi vuoti mostrano un suggerimento, così si capisce cosa scrivere e non si rischia di
 * pubblicare un testo di esempio.
 */
export interface SectionTemplate {
    id: string;
    name: string;
    hint: string;
    icon: string;
    build: () => ContentSection;
}

const block = <T extends ToolDetailBlockType>(type: T, patch: Partial<Extract<ToolDetailBlock, { type: T }>> = {}) =>
    ({ ...createEmptyBlock(type), ...patch }) as ToolDetailBlock;

const times = <T,>(count: number, make: () => T) => Array.from({ length: count }, make);

export const SECTION_TEMPLATES: SectionTemplate[] = [
    {
        id: 'opening', name: 'Apertura', hint: 'Titolo grande, sottotitolo e pulsante', icon: 'bi-megaphone',
        build: () => ({ background: 'brand', align: 'center', blocks: [block('heading'), block('button', { url: '/login' })] }),
    },
    {
        id: 'openingImage', name: 'Apertura con immagine', hint: 'Testo a sinistra, schermata a destra', icon: 'bi-window-sidebar',
        build: () => ({ background: 'brand', blocks: [block('mediaText', { imageSide: 'right' })] }),
    },
    {
        id: 'features', name: 'Funzioni', hint: 'Titolo e tre schede con icona', icon: 'bi-grid-3x3-gap',
        build: () => ({ blocks: [block('heading'), block('cards')] }),
    },
    {
        id: 'steps', name: 'Come funziona', hint: 'Titolo e tre passi numerati', icon: 'bi-list-ol',
        build: () => ({ background: 'soft', blocks: [block('heading'), block('steps', { items: times(3, () => ({ title: '', text: '' })) })] }),
    },
    {
        id: 'stats', name: 'Numeri chiave', hint: 'Tre numeri in evidenza', icon: 'bi-123',
        build: () => ({ align: 'center', blocks: [block('stats')] }),
    },
    {
        id: 'mediaText', name: 'Testo e immagine', hint: 'Un paragrafo accanto a un’immagine', icon: 'bi-layout-text-sidebar-reverse',
        build: () => ({ blocks: [block('mediaText', { imageSide: 'left' })] }),
    },
    {
        id: 'quote', name: 'Testimonianza', hint: 'Una citazione di un cliente', icon: 'bi-quote',
        build: () => ({ background: 'soft', width: 'narrow', blocks: [block('quote')] }),
    },
    {
        id: 'video', name: 'Video', hint: 'Titolo e video di YouTube o Vimeo', icon: 'bi-play-btn',
        build: () => ({ width: 'narrow', blocks: [block('heading', { level: 3 }), block('video')] }),
    },
    {
        id: 'faq', name: 'Domande frequenti', hint: 'Titolo e tre domande', icon: 'bi-question-circle',
        build: () => ({ width: 'narrow', blocks: [block('heading'), block('faq', { items: times(3, () => ({ q: '', a: '' })) })] }),
    },
    {
        id: 'logos', name: 'Clienti', hint: 'I loghi di chi lo usa', icon: 'bi-buildings',
        build: () => ({ align: 'center', blocks: [block('logos', { items: times(4, () => ({ url: '', alt: '' })) })] }),
    },
    {
        id: 'closing', name: 'Invito finale', hint: 'Sezione scura con titolo e pulsante', icon: 'bi-flag',
        build: () => ({ background: 'dark', align: 'center', blocks: [block('heading'), block('button', { url: '/login', style: 'secondary' })] }),
    },
    {
        id: 'chapter', name: 'Capitolo della guida', hint: 'Titolo di capitolo, testo e passi', icon: 'bi-bookmark',
        build: () => ({ blocks: [block('section'), block('richText'), block('steps', { items: times(3, () => ({ title: '', text: '' })) })] }),
    },
];

/** I modelli usabili in un contenuto: tutti i loro blocchi devono essere ammessi lì. */
export function templatesFor(kind: ToolContentKind): SectionTemplate[] {
    const allowed = new Set<string>(BLOCKS_BY_KIND[kind]);
    return SECTION_TEMPLATES.filter(t => t.build().blocks.every(b => allowed.has(b.type)));
}
